import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { App as AntdApp, ConfigProvider } from "antd";
import { getAntdThemeConfig } from "../themeTokens";
import { trackException } from "../observability/telemetry";

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a CustomThemeProvider");
  }
  return context;
};

const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem("themeMode");
      return savedMode === "dark" ? "dark" : "light";
    } catch (error) {
      return "light";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("themeMode", mode);
      document.documentElement.dataset.theme = mode;
      document.documentElement.style.colorScheme = mode;
    } catch (error) {
      trackException(error, { source: "theme.save_preference" });
    }
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const themeConfig = useMemo(() => getAntdThemeConfig(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      toggleColorMode,
      themeConfig,
    }),
    [mode, themeConfig]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={themeConfig} componentSize="large">
        {/*
          `component={false}` supplies the App context (modal/message/notification)
          without rendering a wrapper div, so the layout tree is unchanged.
          Dialogs raised through `App.useApp()` inherit the ConfigProvider theme,
          which the static `Modal.confirm` API cannot see.
        */}
        <AntdApp component={false}>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default CustomThemeProvider;
